import { useCallback, useEffect, useState } from "react";
import { createScopedLogger } from "../logger";

const PERSISTED_SETTING_CHANGED_EVENT = "persisted-setting-changed";
const logger = createScopedLogger("usePersistedSetting");

type PersistedSettingChangedEvent<T> = CustomEvent<{
  storageKey: string;
  value: T;
}>;

/**
 * Persists a single typed setting to localStorage.
 *
 * **NOTE: By default, this hook is NOT reactive across components/tabs.**
 * Each component using this hook maintains its own independent state.
 * Changes made in one component will NOT trigger re-renders in other components
 * using the same storageKey.
 *
 * To enable synchronization, set `syncAcrossTabs` to `true`.
 * This will listen to storage events and same-window setting events so other hook
 * instances using the same storageKey update when the setting changes.
 *
 * @param storageKey      - localStorage key to read/write
 * @param defaultValue    - value used when nothing is stored yet
 * @param syncAcrossTabs  - enable setting synchronization (default: false)
 * @returns [value, setValue] — same shape as useState, but changes are persisted
 */
export function usePersistedSetting<T>(storageKey: string, defaultValue: T, syncAcrossTabs: boolean = false): [T, (value: T | ((prev: T) => T)) => void] {
  const [value, setValueState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) return JSON.parse(stored) as T;
    } catch {}
    return defaultValue;
  });

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValueState((prev) => {
        const resolved = typeof next === "function" ? (next as (prev: T) => T)(prev) : next;
        try {
          localStorage.setItem(storageKey, JSON.stringify(resolved));
          window.dispatchEvent(
            new CustomEvent(PERSISTED_SETTING_CHANGED_EVENT, {
              detail: { storageKey, value: resolved },
            })
          );
        } catch {}
        return resolved;
      });
    },
    [storageKey]
  );

  // Listen for storage events and same-window events when synchronization is enabled.
  useEffect(() => {
    if (!syncAcrossTabs) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey && e.newValue !== null) {
        try {
          const newValue = JSON.parse(e.newValue) as T;
          setValueState(newValue);
        } catch (error) {
          logger.warn(`Failed to parse storage event for key "${storageKey}":`, error);
        }
      }
    };

    const handlePersistedSettingChange = (e: Event) => {
      const customEvent = e as PersistedSettingChangedEvent<T>;
      if (customEvent.detail.storageKey === storageKey) {
        setValueState(customEvent.detail.value);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(PERSISTED_SETTING_CHANGED_EVENT, handlePersistedSettingChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(PERSISTED_SETTING_CHANGED_EVENT, handlePersistedSettingChange);
    };
  }, [storageKey, syncAcrossTabs]);

  return [value, setValue];
}
